import { UnknownSchema } from '../../constants'
import { SchemaUnionEmptyError, SchemaUnionTypesError } from '../../errors'
import { SchemaDefinition } from '../../models/SchemaDefinition'

import type {
  RuntimeSchemaSpecific,
  SchemaUnionSpecific,
  TypedSchema
} from '../../types'

export class SchemaUnionDefinition extends SchemaDefinition {
  override defaultsMerge(
    value: any,
    schema: SchemaUnionSpecific,
    initialize: boolean
  ) {
    if (schema.types?.[0] === undefined) return value
    return super.defaultsMerge(value, schema.types?.[0], initialize)
  }

  override getIn(
    schema: SchemaUnionSpecific,
    path: PropertyKey[]
  ): TypedSchema {
    if (path.length === 0) return schema
    for (const type of schema.types ?? []) {
      const result = super.getIn(type, path)
      if (result !== UnknownSchema) return result
    }
    return UnknownSchema
  }

  override infer(
    target: any,
    schema?: SchemaUnionSpecific,
    path: PropertyKey[] = []
  ): TypedSchema {
    for (const type of schema?.types ?? []) {
      if (super.test(target, type, path)) {
        return this.__r_hoistMetadata(super.infer(target, type, path), schema)
      }
    }
    return this.__r_hoistMetadata(UnknownSchema, schema)
  }

  override inferOnlySchema(
    schema: SchemaUnionSpecific,
    path: PropertyKey[] = []
  ): SchemaUnionSpecific {
    return {
      ...schema,
      types: schema.types?.map((type) => super.inferOnlySchema(type, path))
    }
  }

  override inferTypeScript(
    schema: SchemaUnionSpecific,
    path: PropertyKey[]
  ): string {
    return super.inferTypeScript(this.infer(schema.value, schema, path), path)
  }

  override prune(target: any): any {
    return target
  }

  override transform(
    target: any,
    schema: SchemaUnionSpecific,
    path: PropertyKey[] = []
  ) {
    if (target === null && schema.nullable) return target
    if (schema.types?.[0] === undefined)
      return super.transform(target, UnknownSchema, path)
    for (const type of schema.types) {
      if (super.test(target, type, path)) {
        return super.transform(target, type, path)
      }
    }
    return super.transform(target, schema.types[0], path)
  }

  override validate(target: any, schema: any, path: PropertyKey[]): void
  override validate(
    target: any,
    schema: RuntimeSchemaSpecific<SchemaUnionSpecific>,
    path: PropertyKey[]
  ): void {
    if (this.validateMetadata(target, schema)) return
    if (schema.types) {
      let validated: boolean | undefined
      this.catch(
        () => {
          validated = schema.types?.some((type) =>
            super.test(target, type, path)
          )
        },
        { isolated: true }
      )
      if (!validated) {
        this.raise(new SchemaUnionTypesError(schema.name))
      }
    } else {
      this.raise(new SchemaUnionEmptyError(schema.name))
    }
  }
}
